import React, { useState, useEffect } from "react";
import { fetchCustomers, updateUser } from "./api/users";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Customer, Role, User } from "types/settings";
import { MultiSelectCombobox } from "../../../components/ui/multi-select-combobox";

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onUserUpdated: () => void;
  roles: Role[];
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open, 
  onOpenChange, 
  user,
  onUserUpdated, 
  roles 
}) => {
  const [updating, setUpdating] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    mobile_no: "",
    email: "",
    cust_ids: [] as string[],
    role_id: "",
    user_type: "",
    is_active: true
  });

  useEffect(() => {
    if (open && user) {
      populateForm();
      loadCustomers();
    }
  }, [open, user]);

  const loadCustomers = async () => {
      try {
          const res = await fetchCustomers();
          setCustomers(res);
      } catch (err) {
          console.error("Failed to load customers", err);
      }
    };

  const populateForm = () => {
    if (!user) return;
    
    setUpdateError("");
    setValidationErrors({});
    
    setFormData({
      name: user.name,
      mobile_no: user.mobile_no,
      email: user.email,
      cust_ids: user.customers?.map(c => c.cust_id) ?? [],
      role_id: user.role_id.toString(),
      user_type: user.user_type ?? "Customer",
      is_active: user.is_active
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    if (field === "mobile_no" && typeof value === "string" && value) {
      const mobileRegex = /^\d{10}$/;
      if (value.length > 0 && !mobileRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          mobile_no: value.length !== 10 ? "Must be exactly 10 digits" : "Only numbers allowed"
        }));
      }
    }

    if (field === "email" && typeof value === "string" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value.length > 0 && !emailRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: "Please enter a valid email address"
        }));
      }
    }
  };

  /**
   * Customer selection logic
   */
  // Demo and non-Demo users pick from disjoint customer lists, so a type change clears
  // the selection instead of carrying over customers the new type can't have.
  const handleUserTypeChange = (value: string) => {
    setFormData(prev => ({ ...prev, user_type: value, cust_ids: [] }));
  };

  const toggleCustomer = (custId: string) => {
    setFormData(prev => {
      if (prev.user_type !== "Internal") {
        return { ...prev, cust_ids: [custId] };
      }

      const exists = prev.cust_ids.includes(custId);

      return {
        ...prev,
        cust_ids: exists
          ? prev.cust_ids.filter(id => id !== custId)
          : [...prev.cust_ids, custId],
      };
    });
  };

  const removeCustomer = (custId: string) => {
    setFormData(prev => ({
      ...prev,
      cust_ids: prev.cust_ids.filter(id => id !== custId),
    }));
  };

  const handleUpdateUser = async () => {
    if (!user) return;

    try {
      setUpdating(true);
      setUpdateError("");

      if (!formData.name.trim()) {
        setUpdateError("Name is required");
        return;
      }

      if (!formData.mobile_no.trim()) {
        setUpdateError("Mobile number is required");
        return;
      }
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile_no.trim())) {
        setUpdateError("Mobile number must be exactly 10 digits");
        return;
      }

      if (!formData.email.trim()) {
        setUpdateError("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setUpdateError("Please enter a valid email address");
        return;
      }

      if (formData.cust_ids.length === 0) {
        setUpdateError("Customer selection is required");
        return;
      }

      if (!formData.role_id) {
        setUpdateError("Role is required");
        return;
      }

      const userData = {
        name: formData.name.trim(),
        mobile_no: formData.mobile_no.trim(),
        email: formData.email.trim(),
        cust_ids: formData.cust_ids,
        role_id: parseInt(formData.role_id),
        user_type: formData.user_type,
      };

      await updateUser(user.user_id, userData);

      // Notify parent component to refresh users list
      if (onUserUpdated) {
        onUserUpdated();
      }

      onOpenChange(false);
    } catch (err: any) {
      console.error("Error updating user:", err);
      let errorMessage = "Failed to update user";
      
      if (err?.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
        else if (Array.isArray(err.response.data.detail) && err.response.data.detail.length > 0) {
          const firstError = err.response.data.detail[0];
          errorMessage = firstError.msg || "Validation error";
        }
      } else if (err?.response?.status === 400 && err?.response?.data) {
        errorMessage = err.response.data.detail || err.response.data.message || "Bad request";
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setUpdateError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  if (!user) return null;

  const isSingleCustomer = formData.user_type !== "Internal";
  // Demo users may only be linked to demo customers, everyone else only to real ones.
  const visibleCustomers = customers.filter(
    (c) => Boolean(c.is_demo) === (formData.user_type === "Demo")
  );

  const selectedCustomers = customers.filter(c =>
    formData.cust_ids.includes(c.cust_id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Edit User</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 space-y-4 pb-1">
          {updateError && (
            <Alert variant="destructive">
              <AlertDescription>{updateError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter full name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number *</Label>
            <Input
              id="mobile"
              value={formData.mobile_no}
              onChange={(e) => handleInputChange('mobile_no', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              className={validationErrors.mobile_no ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.mobile_no && (
              <p className="text-sm text-red-600">{validationErrors.mobile_no}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className={validationErrors.email ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">{validationErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>User Type *</Label>
            <Select
              value={formData.user_type}
              onValueChange={handleUserTypeChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer">Customer</SelectItem>
                <SelectItem value="Internal">Internal</SelectItem>
                <SelectItem value="Demo">Demo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Customers */}
          <div className="space-y-2">
            <Label>
              {isSingleCustomer ? "Customer *" : "Customers *"}
            </Label>

            <MultiSelectCombobox
              options={visibleCustomers.map(c => ({
                value: c.cust_id,
                label: c.cust_name,
              }))}
              value={formData.cust_ids}
              onValueChange={(values) => {
                if (isSingleCustomer) {
                  setFormData(prev => ({
                    ...prev,
                    cust_ids: values.length
                      ? [values[values.length - 1]]
                      : [],
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    cust_ids: values,
                  }));
                }
              }}
              placeholder="Select customers"
              searchPlaceholder="Search customers..."
              emptyText="No customers found"
            />

            {selectedCustomers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedCustomers.map(c => (
                  <span
                    key={c.cust_id}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    {c.cust_name}
                    <button
                      type="button"
                      onClick={() => removeCustomer(c.cust_id)}
                      className="ml-1 text-muted-foreground hover:text-destructive"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-medium">Role *</Label>
            <Select value={formData.role_id} onValueChange={(value) => handleInputChange('role_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.role_id} value={role.role_id.toString()}>
                    {role.role_desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} disabled={updating}>
            {updating ? 'Updating...' : 'Update User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
